"use client";

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { AnimatePresence, motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import { SearchCriteriaCard } from "./criteria-card";
import { SearchCriteriaEditor } from "./criteria-editor";
import {
  MAX_CRITERIA,
  useSearchCriteriaDrafts,
  type SearchCriteriaDraftsState,
} from "./use-criteria-drafts";

export { type SearchCriterionDraft } from "./use-criteria-drafts";

const EASE = [0.4, 0, 0.2, 1] as const;
const SPRING = {
  type: "spring" as const,
  damping: 30,
  stiffness: 500,
  mass: 0.5,
};

function cardPosition(index: number, total: number) {
  if (total === 1) return "single" as const;
  if (index === 0) return "first" as const;
  if (index === total - 1) return "last" as const;
  return "middle" as const;
}

export interface SearchCriteriaListProps {
  /** Called after the rows have been applied to the search draft. */
  onSaved?: () => void;
}

/**
 * Self-contained ranking-criteria editor (list + Apply) used by the toolbar
 * sheet; must render inside `TalentSearchResultsProvider`. The filter panel
 * composes `SearchCriteriaListView` and its own fixed footer from
 * `useSearchCriteriaDrafts` instead.
 */
export function SearchCriteriaList({ onSaved }: SearchCriteriaListProps) {
  const state = useSearchCriteriaDrafts({ onSaved });

  return (
    <div className="flex w-full flex-col gap-4">
      <SearchCriteriaListView state={state} />
      <div className="flex justify-end">
        <Button
          type="button"
          variant="primary"
          size="sm"
          onClick={state.save}
          disabled={!state.dirty}
        >
          Apply to draft
        </Button>
      </div>
    </div>
  );
}

/**
 * Ordered criteria cards on a continuous spine — drag to reorder, edit in
 * place, pill-to-form add — mirroring the Arbitrator setup list.
 */
export function SearchCriteriaListView({
  state,
}: {
  state: SearchCriteriaDraftsState;
}) {
  const { drafts, adding, setAdding, atCap, add, update, remove, move } = state;
  const [activeId, setActiveId] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveId(null);
      const { active, over } = event;
      if (!over) return;
      move(active.id as string, over.id as string);
    },
    [move],
  );

  const handleDragCancel = useCallback(() => setActiveId(null), []);

  // Continuous vertical spine behind the cards — measured via
  // ResizeObserver exactly like the Arbitrator setup list.
  const listRef = useRef<HTMLDivElement>(null);
  const addFormRef = useRef<HTMLDivElement>(null);
  const [lineBounds, setLineBounds] = useState<{
    top: number;
    bottom: number;
  } | null>(null);

  useLayoutEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const nodes = new Set<Element>();

    const recompute = () => {
      const items = el.querySelectorAll<HTMLElement>("[data-criteria-node]");
      if (items.length < 2) {
        setLineBounds(null);
        return;
      }
      const containerRect = el.getBoundingClientRect();
      const firstRect = items[0].getBoundingClientRect();
      const lastRect = items[items.length - 1].getBoundingClientRect();
      setLineBounds({
        top: firstRect.top - containerRect.top + firstRect.height / 2,
        bottom: containerRect.bottom - lastRect.bottom + lastRect.height / 2,
      });
      for (const item of items) {
        if (!nodes.has(item)) {
          nodes.add(item);
          ro.observe(item);
        }
      }
    };

    const ro = new ResizeObserver(recompute);
    ro.observe(el);
    recompute();
    return () => ro.disconnect();
  }, [drafts, adding]);

  const activeDraft = activeId ? drafts.find((d) => d.id === activeId) : null;
  const isEmpty = drafts.length === 0;

  return (
    <div className="flex w-full flex-col gap-3">
      <div
        ref={listRef}
        className="relative flex w-full flex-col items-center"
        style={{ gap: 30 }}
      >
        {lineBounds && (
          <div
            aria-hidden
            className="pointer-events-none absolute left-1/2 w-px -translate-x-1/2 bg-foreground/25 dark:bg-[#343436]"
            style={{ top: lineBounds.top, bottom: lineBounds.bottom }}
          />
        )}

        {isEmpty ? (
          <EmptyState
            adding={adding}
            onStart={() => setAdding(true)}
            onCancel={() => setAdding(false)}
            onSaved={add}
          />
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
            modifiers={[restrictToVerticalAxis]}
          >
            <SortableContext
              items={drafts.map((d) => d.id)}
              strategy={verticalListSortingStrategy}
            >
              {drafts.map((d, i) => {
                const pos = cardPosition(i, drafts.length);
                const isFirst = pos === "first" || pos === "single";
                const isLast = pos === "last" || pos === "single";

                return (
                  <div
                    key={d.id}
                    className="relative z-[1] w-full"
                    data-criteria-node=""
                  >
                    {isFirst && (
                      <div className="flex w-full items-center rounded-t-lg border-l border-r border-t border-contrast/10 bg-[#f9f9fb] px-4 py-1.5 text-[13px] text-contrast/70 dark:bg-[#252527]">
                        Most important
                      </div>
                    )}

                    <SearchCriteriaCard
                      draft={d}
                      position={pos}
                      onUpdate={update}
                      onRemove={remove}
                    />

                    {isLast && (
                      <div className="flex w-full items-center rounded-b-lg border-b border-l border-r border-contrast/10 bg-[#f9f9fb] px-4 py-1.5 text-[13px] text-contrast/70 dark:bg-[#252527]">
                        Least important
                      </div>
                    )}
                  </div>
                );
              })}
            </SortableContext>

            {mounted &&
              createPortal(
                <DragOverlay dropAnimation={null}>
                  {activeDraft ? (
                    <div className="opacity-90">
                      <SearchCriteriaCard
                        draft={activeDraft}
                        position="middle"
                        onUpdate={() => {}}
                        onRemove={() => {}}
                        isDragOverlay
                      />
                    </div>
                  ) : null}
                </DragOverlay>,
                document.body,
              )}
          </DndContext>
        )}

        {!isEmpty && (
          <motion.div
            className="group relative z-[1] flex shrink-0 overflow-hidden border border-contrast/10 bg-white shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] dark:bg-[#2a2a2c]"
            variants={{
              closed: { width: "auto", borderRadius: "10rem" },
              open: { width: "100%", borderRadius: "0.5rem" },
            }}
            initial="closed"
            animate={adding ? "open" : "closed"}
            transition={{ duration: 0.5 }}
            data-criteria-node=""
          >
            {!adding && (
              <span className="pointer-events-none absolute inset-0 rounded-[inherit] bg-contrast/4 opacity-0 transition-opacity group-hover:opacity-100" />
            )}
            <AnimatePresence mode="wait" initial={false}>
              {!adding ? (
                <motion.button
                  key="pill-button"
                  type="button"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3, ease: EASE }}
                  className="flex h-8 w-full cursor-pointer items-center justify-center gap-1.5 px-2 text-center disabled:cursor-not-allowed disabled:opacity-40"
                  onClick={() => {
                    if (atCap) return;
                    setAdding(true);
                  }}
                  disabled={atCap}
                >
                  <Icon name="IconPlusMedium" size={16} className="text-heading" />
                  <span className="whitespace-nowrap text-sm leading-5 text-heading">
                    Add a criteria
                  </span>
                </motion.button>
              ) : (
                <motion.div
                  key="create-form"
                  ref={addFormRef}
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3, ease: EASE }}
                  // The form opens at the bottom of the list, usually below
                  // the fold — bring it into view once it has its full height.
                  onAnimationComplete={(def) => {
                    // Only after the open (height → auto) animation, not exit.
                    if (
                      typeof def !== "object" ||
                      (def as { height?: unknown }).height !== "auto"
                    )
                      return;
                    addFormRef.current?.scrollIntoView({
                      block: "nearest",
                      behavior: "smooth",
                    });
                  }}
                  className="w-full overflow-hidden"
                >
                  <SearchCriteriaEditor
                    onCancel={() => setAdding(false)}
                    onSaved={add}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </div>

      {atCap && (
        <p className="text-xs text-contrast/50">
          Maximum of {MAX_CRITERIA} criteria.
        </p>
      )}
    </div>
  );
}

function EmptyState({
  adding,
  onStart,
  onCancel,
  onSaved,
}: {
  adding: boolean;
  onStart: () => void;
  onCancel: () => void;
  onSaved: (item: { label: string; description: string }) => void;
}) {
  return (
    <AnimatePresence mode="wait">
      {adding ? (
        <motion.div
          key="editor"
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={SPRING}
          className="w-full overflow-hidden rounded-lg border border-contrast/10 bg-white shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] dark:bg-[#2a2a2c]"
        >
          <SearchCriteriaEditor onCancel={onCancel} onSaved={onSaved} />
        </motion.div>
      ) : (
        <motion.div
          key="empty"
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 6 }}
          transition={SPRING}
          className="flex w-full flex-col items-center justify-center"
        >
          <div className="relative w-full overflow-hidden rounded-lg border border-dashed border-contrast/15 bg-[#f9f9fb] p-4 shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] dark:bg-[#2d2d2f]">
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 rounded-lg dark:shadow-[inset_0px_-1px_0px_0px_rgba(0,0,0,0.1)]"
            />
            <div className="relative flex flex-col items-start gap-2.5">
              <div className="flex h-7 items-center gap-2">
                <button
                  type="button"
                  onClick={onStart}
                  aria-label="Add your first criteria"
                  className="inline-flex size-7 cursor-pointer items-center justify-center rounded-full bg-contrast/6 text-heading shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05),inset_0px_-1px_0px_0px_rgba(0,0,0,0.08)] transition-colors hover:bg-contrast/10 dark:bg-[#232325] dark:hover:bg-[#2a2a2c]"
                >
                  <Icon name="IconPlusMedium" size={16} />
                </button>
                <button
                  type="button"
                  onClick={onStart}
                  className="inline-flex cursor-pointer items-center rounded-full bg-contrast/6 px-2.5 py-1 text-sm font-medium text-heading shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05),inset_0px_-1px_0px_0px_rgba(0,0,0,0.08)] transition-colors hover:bg-contrast/10 dark:bg-[#232325] dark:hover:bg-[#2a2a2c]"
                >
                  Add your first criteria
                </button>
              </div>
              <p className="text-[13px] text-contrast/70">
                Criteria rank every profile in this search. Start with one.
              </p>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
